/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/no-multi-comp */
import React, { ReactElement, useMemo } from 'react';
import {
  KeyboardTimePicker,
  KeyboardDatePicker,
  MenuItem,
  Select,
  TextField,
  IOutlinedTextFieldProps,
  ITextFieldProps,
} from '~/UIKit';
import { CHARCOAL_GREY, DARKER_GREY, PRIMARY_BLUE } from '../../../Constants/Colors.constants';
import { Field } from 'formik';
import { useTranslation } from 'react-i18next';
import { ItemCreationMoney } from './ItemCreationMoney';
import { EventTrainerSelection } from './EventTrainerSelection/EventTrainerSelection';
import { EventLocationSelection } from './EventLocationSelection';
import classNames from 'classnames';
import { ISelectOptions } from '~/Models/interfaces/SelectOption.models';
import { useDateFns } from '~/Hooks';
import { ItemCreationColorPicker } from './ItemCreationColorPicker';
import { ItemCreationValidInput } from './ItemCreationSubsectionInput.model';
import { ItemCreationFieldValue } from '~/Components';
import { EventBookingGroupSelection } from './EventBookingGroupSelection';
import { Switcher } from '~/UIKit/Form/Switcher';
import { selectClasses } from '@mui/material';

import { makeStyles } from '~/UIKit/Style';
import { ItemCreationCounter } from './ItemCreationCounter';
import { ItemCreationMultiSelect } from './ItemCreationMultiSelect';
const useStyles = makeStyles({
  ItemCreationSubsectionInputElement: {
    marginLeft: 'auto',
    color: PRIMARY_BLUE,
    '& input': {
      textAlign: 'right',
    },
  },
  ItemCreationSubsectionInputContainer: {
    color: CHARCOAL_GREY,
    width: '100%',
  },
  ItemCreationSubsectionInputPeriodNumber: {
    maxWidth: 60,
    marginLeft: 'auto',
  },
  ItemCreationSubsectionInputPeriodSelect: {
    maxWidth: 100,
    marginLeft: 15,
    color: 'currentColor',

    [`& .${selectClasses['select']}`]: {
      border: 'thin solid',
      borderColor: DARKER_GREY,
      borderRadius: 5,
      padding: '4px 24px 4px 5px',
    },
  },
  ItemCreationSubsectionInputElementAdornment: {
    color: DARKER_GREY,
    marginBottom: 4,
  },
  ItemCreationSubsectionInputPeriodContainer: {
    display: 'flex',
    marginLeft: 'auto',
    alignContent: 'space-between',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  ItemCreationSubsectionInputMultiSelect: {
    minWidth: 100,
  },
});

export interface IUseItemCreationSubsectionInput<T> {
  type?: ItemCreationValidInput;
  name?: keyof T;
  icon?: ReactElement;
  label?: string;
  placeholder?: string;
  secondaryName?: keyof T;
  secondaryPlaceholder?: string;
  secondaryDisabled?: boolean;
  disabled?: boolean;
  options?: ISelectOptions;
  InputProps?: ITextFieldProps | IOutlinedTextFieldProps;
  width?: number;
}

interface ExtraFormikFields {
  error: string;
  value: any;
  dirty: boolean;
  touched: boolean;
  disabled: boolean;
  secondaryError: string;
  secondaryValue: any;
  secondaryDirty: boolean;
  secondaryDisabled: boolean;
  setFieldValue: ItemCreationFieldValue;
  InputProps?: ITextFieldProps | IOutlinedTextFieldProps;
}

export const useItemCreationSubsectionInput = <T extends object>({
  type,
  name: nameSymbol,
  icon,
  label,
  options,
  placeholder,
  secondaryName = '' as any,
  secondaryPlaceholder,
  secondaryDisabled,
  InputProps,
  disabled,
}: IUseItemCreationSubsectionInput<T>) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { localeDateFormat } = useDateFns();
  const name = String(nameSymbol);

  return useMemo(() => {
    const handleBlurNumbers = (setFieldValue: ItemCreationFieldValue, value: any) => () => {
      if (!value) {
        setFieldValue(name as string, 0);
      }
    };
    switch (type) {
      case ItemCreationValidInput.NUMBER:
        return ({
          error,
          touched,
          value,
          disabled: formDisabled,
          InputProps,
          setFieldValue,
        }: ExtraFormikFields) => (
          <Field
            key={name}
            className={classes.ItemCreationSubsectionInputContainer}
            component={TextField}
            fullWidth
            name={name}
            value={value}
            placeholder={placeholder || label}
            type="number"
            label={label}
            disabled={disabled || formDisabled}
            error={touched && !!error}
            newDesign
            icon={icon}
            onBlur={handleBlurNumbers(setFieldValue, value)}
            {...InputProps}
          />
        );

      case ItemCreationValidInput.TEXT:
        return ({ error, touched, value, disabled: formDisabled }: ExtraFormikFields) => (
          <Field
            className={classNames(classes.ItemCreationSubsectionInputContainer)}
            component={TextField}
            fullWidth
            name={name}
            placeholder={placeholder || label}
            type="text"
            label={label}
            value={value}
            error={touched && !!error}
            disabled={disabled || formDisabled}
            newDesign
            icon={icon}
            {...InputProps}
          />
        );

      case ItemCreationValidInput.SWITCHER:
        return ({ value, disabled: formDisabled }: ExtraFormikFields) => (
          <Field
            data-testid={`switcher.${name as string}`}
            className={classes.ItemCreationSubsectionInputElement}
            component={Switcher}
            name={name}
            checked={value}
            disabled={disabled || formDisabled}
            width={InputProps?.style?.width}
            placeholder={placeholder}
            secondaryPlaceholder={secondaryPlaceholder}
          />
        );

      case ItemCreationValidInput.PERIOD:
        return ({
          value,
          secondaryValue,
          disabled: formDisabled,
          setFieldValue,
        }: ExtraFormikFields) => (
          <div className={classes.ItemCreationSubsectionInputPeriodContainer}>
            {secondaryValue !== 'Unlimited' && (
              <Field
                className={classes.ItemCreationSubsectionInputPeriodNumber}
                component={TextField}
                fullWidth
                name={name}
                placeholder={placeholder}
                type="number"
                onBlur={handleBlurNumbers(setFieldValue, value)}
                newDesign
                value={value}
                disabled={disabled || formDisabled}
              />
            )}
            <Field
              className={classes.ItemCreationSubsectionInputPeriodSelect}
              component={Select}
              fullWidth
              name={secondaryName}
              placeholder={secondaryPlaceholder}
              type="select"
              newDesign
              disabled={secondaryDisabled}
              value={secondaryValue}
            >
              <MenuItem value="Weeks">{t('app.weeks')}</MenuItem>
              <MenuItem value="Month">{t('app.months')}</MenuItem>
              <MenuItem value="Years">{t('app.years')}</MenuItem>
              <MenuItem value="Unlimited">{t('app.unlimited')}</MenuItem>
            </Field>
          </div>
        );

      case ItemCreationValidInput.LIMITED_PERIOD:
        return ({
          value,
          secondaryValue,
          disabled: formDisabled,
          setFieldValue,
        }: ExtraFormikFields) => (
          <div className={classes.ItemCreationSubsectionInputPeriodContainer}>
            <Field
              className={classes.ItemCreationSubsectionInputPeriodNumber}
              component={TextField}
              fullWidth
              name={name}
              placeholder={placeholder}
              type="number"
              onBlur={handleBlurNumbers(setFieldValue, value)}
              value={value}
              newDesign
              disabled={disabled || formDisabled}
            />
            <Field
              className={classes.ItemCreationSubsectionInputPeriodSelect}
              component={Select}
              fullWidth
              name={secondaryName}
              placeholder={secondaryPlaceholder}
              type="select"
              newDesign
              disabled={secondaryDisabled}
              value={secondaryValue}
            >
              <MenuItem value="Weekly">{t('app.weeks')}</MenuItem>
              <MenuItem value="Monthly">{t('app.months')}</MenuItem>
              <MenuItem value="Quarterly">{t('app.quarters')}</MenuItem>
              <MenuItem value="Yearly">{t('app.years')}</MenuItem>
            </Field>
          </div>
        );

      case ItemCreationValidInput.MONEY:
        return ({ value, secondaryValue, setFieldValue }: ExtraFormikFields) =>
          ItemCreationMoney({
            name: name as string,
            value,
            placeholder,
            secondaryName: secondaryName as string,
            secondaryValue,
            setFieldValue,
          });

      case ItemCreationValidInput.COLOR:
        return ({ value, setFieldValue, disabled: formDisabled }: ExtraFormikFields) => (
          <ItemCreationColorPicker
            setFieldValue={setFieldValue}
            fieldName={name as string}
            color={value as string}
            disabled={disabled || formDisabled}
          />
        );
      case ItemCreationValidInput.TRAINER_SELECTOR:
        return ({ value, setFieldValue, disabled: formDisabled }: ExtraFormikFields) => (
          <EventTrainerSelection
            setFieldValue={setFieldValue}
            name={name as string}
            selectedTrainerIds={value as string[]}
            disabled={disabled || formDisabled}
          >
            {placeholder}
          </EventTrainerSelection>
        );

      case ItemCreationValidInput.LOCATION_SELECTOR:
        return ({ value, setFieldValue, disabled: formDisabled }: ExtraFormikFields) => (
          <EventLocationSelection
            setFieldValue={setFieldValue}
            name={name as string}
            selectedLocationId={value as string}
            disabled={disabled || formDisabled}
          >
            {placeholder}
          </EventLocationSelection>
        );

      case ItemCreationValidInput.BOOKINGGROUP_SELECTOR:
        return ({ value, setFieldValue }: ExtraFormikFields) => (
          <EventBookingGroupSelection
            setFieldValue={setFieldValue}
            name={name as string}
            selectedBookingGroupIds={value as string[]}
          >
            {placeholder}
          </EventBookingGroupSelection>
        );

      case ItemCreationValidInput.DATE:
        return ({ value, setFieldValue, error, disabled: formDisabled }: ExtraFormikFields) => (
          <KeyboardDatePicker
            inputFormat={InputProps?.inputFormat || localeDateFormat}
            label={label}
            icon={icon}
            name={name as string}
            onDateChange={setFieldValue}
            error={!!error}
            defaultValue={value as string}
            value={value as string}
            minDate={InputProps?.inputProps?.min}
            disabled={disabled || formDisabled}
            disablePast={InputProps?.inputProps?.disablePast}
          />
        );

      case ItemCreationValidInput.TIME:
        return ({ value, setFieldValue, error, disabled: formDisabled }: ExtraFormikFields) => (
          <KeyboardTimePicker
            inputFormat="HH:mm"
            label={label}
            icon={icon}
            name={name as string}
            onDateChange={setFieldValue}
            error={!!error}
            defaultValue={value as string}
            value={value as string}
            minTime={InputProps?.inputProps?.min}
            disabled={disabled || formDisabled}
          />
        );

      case ItemCreationValidInput.SELECT:
        return ({ value, disabled: formDisabled }: ExtraFormikFields) => (
          <Field
            className={classes.ItemCreationSubsectionInputContainer}
            component={TextField}
            fullWidth
            name={name}
            value={value || ''}
            placeholder={placeholder || label}
            label={label}
            newDesign
            icon={icon}
            disabled={disabled || formDisabled}
            select
            {...InputProps}
          >
            {options?.map((option, index) => (
              <MenuItem value={option.value} key={`${String(name)}_${index}`}>
                {option.label}
              </MenuItem>
            ))}
          </Field>
        );
      case ItemCreationValidInput.LOCATION_SELECTOR_MULTI:
        return ({ value, disabled: formDisabled, setFieldValue }: ExtraFormikFields) => (
          <ItemCreationMultiSelect
            classes={classes}
            name={name}
            value={value || []}
            label={label}
            renderValue={InputProps?.inputProps?.formatValue || undefined}
            disabled={disabled || formDisabled}
            setFieldValue={setFieldValue}
            options={options}
          />
        );
      case ItemCreationValidInput.COUNTER:
        return ({ value, setFieldValue }: ExtraFormikFields) => (
          <ItemCreationCounter
            className={classes.ItemCreationSubsectionInputContainer}
            value={value}
            min={InputProps?.inputProps?.min}
            max={InputProps?.inputProps?.max}
            formatValue={InputProps?.inputProps?.formatValue}
            onUpdate={(newValue: number) => setFieldValue(String(name), newValue)}
          />
        );
      default:
        return () => <div />;
    }
  }, [options, disabled, InputProps?.inputProps?.min, InputProps?.inputProps?.max]);
};
